import { Link } from 'react-router-dom';
import { formatDistance, haversineMeters } from '@wineryxplorer/shared';
import type { PrimaryAtmosphere, Amenity, DistanceLocale } from '@wineryxplorer/shared';
import { ImgWithSkeleton } from '../ui/ImgWithSkeleton';

export interface CompactVenueCardProps {
  venue: {
    _id: string;
    name: string;
    venueType: string;
    address?: {
      city?: string;
      state?: string;
      coordinates?: { lat: number; lng: number } | null;
    };
    priceRange?: number | null;
    primaryAtmosphere?: PrimaryAtmosphere | null;
    amenities?: Amenity[];
    firstHeroImage?: { cloudinaryUrl: string } | null;
    logoImage?: { cloudinaryUrl: string } | null;
    distance?: number;
  };
  userLocation?: { lat: number; lng: number } | null;
  distanceLocale?: DistanceLocale;
}

const AMENITY_LABELS: Record<Amenity, string> = {
  'scenic-views': 'Scenic Views',
  'historic-building': 'Historic',
  'family-friendly': 'Family Friendly',
  'pet-friendly': 'Pet Friendly',
  'outdoor-seating': 'Outdoor Seating',
  'live-music': 'Live Music',
  'food-service': 'Food Service',
  'tours-available': 'Tours',
  'event-space': 'Event Space',
  'wheelchair-accessible': 'Accessible',
};

// Compact list card — a scaled-down sibling of FeaturedWineryCard on the
// home page. Split is ~45/55 photo/info. Falls back to a warm paper
// placeholder when no hero image is available (logos are NOT used here —
// many venues lack logos and inconsistent thumbnails looked ugly).
export const CompactVenueCard = ({ venue, userLocation, distanceLocale = 'imperial' }: CompactVenueCardProps) => {
  const heroUrl = venue.firstHeroImage?.cloudinaryUrl;

  const priceLabel = venue.priceRange ? '$'.repeat(venue.priceRange) : null;

  // Distance in meters: prefer server value, else compute client-side
  const distanceMeters =
    venue.distance ??
    (userLocation && venue.address?.coordinates
      ? haversineMeters(userLocation, venue.address.coordinates)
      : undefined);

  const distanceDisplay = distanceMeters != null
    ? formatDistance(distanceMeters, distanceLocale)
    : undefined;

  const hasCity = venue.address?.city && venue.address?.state;

  return (
    <Link
      to={`/wineries/${venue._id}`}
      className="group block overflow-hidden rounded-lg bg-gray-100 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Fixed card height — hero image covers/clips, text side has a
          predictable footprint. Without this, portrait-aspect heroes stretch
          the card to their intrinsic height. */}
      <div className="flex items-stretch h-40 sm:h-44">
        {/* Photo — ~45% width, full card height, cover */}
        <div className="flex-shrink-0 w-[42%] sm:w-[40%] overflow-hidden bg-gray-100">
          {heroUrl ? (
            <ImgWithSkeleton
              src={heroUrl}
              alt={venue.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              skeletonClassName=""
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
              </svg>
            </div>
          )}
        </div>

        {/* Info — single column on mobile, two-column with right rail on sm+ */}
        <div className="flex-1 min-w-0 flex gap-3 p-3 sm:p-4">
          {/* Main info */}
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <h3 className="font-serif text-base sm:text-xl font-normal leading-tight text-gray-900 group-hover:text-gray-700 transition-colors line-clamp-2">
              {venue.name}
            </h3>

            {/* Mobile-only inline meta: city + distance under the name */}
            {(hasCity || distanceDisplay) && (
              <div className="sm:hidden mt-1 flex items-baseline gap-2 text-[0.65rem] tracking-widest uppercase text-gray-500 leading-tight">
                {hasCity && (
                  <span className="truncate">
                    {venue.address!.city}, {venue.address!.state}
                  </span>
                )}
                {distanceDisplay && (
                  <span className="flex-shrink-0 normal-case tracking-normal text-xs text-primary-700 font-medium">
                    {distanceDisplay.display}
                  </span>
                )}
              </div>
            )}

            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200 capitalize">
                {venue.venueType}
              </span>
              {venue.primaryAtmosphere && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-800 border border-primary-200 capitalize">
                  {venue.primaryAtmosphere}
                </span>
              )}
              {priceLabel && (
                <span className="sm:hidden text-xs font-semibold tracking-wider text-gray-900">
                  {priceLabel}
                </span>
              )}
            </div>

            {venue.amenities && venue.amenities.length > 0 && (
              <div className="hidden sm:flex items-center gap-1.5 mt-2 flex-wrap">
                {venue.amenities.slice(0, 3).map((a) => (
                  <span key={a} className="text-xs text-gray-500">
                    {AMENITY_LABELS[a]}
                  </span>
                ))}
                {venue.amenities.length > 3 && (
                  <span className="text-xs text-gray-400">+{venue.amenities.length - 3}</span>
                )}
              </div>
            )}
          </div>

          {/* Right rail (desktop only): price / distance / city stacked */}
          <div className="hidden sm:flex flex-shrink-0 flex-col items-end justify-center text-right gap-1 min-w-[5rem]">
            {priceLabel && (
              <span className="text-sm font-semibold tracking-wider text-gray-900">
                {priceLabel}
              </span>
            )}
            {distanceDisplay && (
              <span className="text-xs text-primary-700 font-medium">
                {distanceDisplay.display}
              </span>
            )}
            {hasCity && (
              <span className="text-[0.7rem] font-sans tracking-widest uppercase text-gray-500 leading-tight">
                {venue.address!.city}, {venue.address!.state}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};
